import {IServiceFactory} from "../../../service-factory.interface";
import { action, observable, makeObservable } from "mobx";
import {ISwipeLogicHandlerMoveNextOptions, ISwipeLogicHandler} from "./swiper-logic-handler.interface";
import {NullableNumber} from "../../../../../common/types/nullable-types";
import {Check} from "@sparkware/app-utils";
import {ISwiperListViewModel} from "../lists/swiper-list-view-model.interface";

export class LiveStreamSwipeLogicHandler implements ISwipeLogicHandler {

    private _element: HTMLElement = null;
    private _touchStartX = 0;
    private _swipeDirection = 0;
    private _touchDragThreshold = 25;
    private _swipeTransitionCss = "transform 0.5s ease-in-out";

    constructor(private readonly _services: IServiceFactory, private readonly _gamesListViewModel: ISwiperListViewModel) {
        makeObservable(this);
    }

    @observable.ref
    allowNext: boolean = true;
    @observable.ref
    allowPrevious: boolean = true;

    private _subscribe() {
        this._unsubscribe();
        this._element.addEventListener("transitionend", this._handleTransitionEnd);
        this._element.addEventListener("touchstart", this._handleTouchStart);
        this._element.addEventListener("touchend", this._handleTouchEnd);
    }

    private _unsubscribe() {
        if (this._element) {
            this._element.removeEventListener("transitionend", this._handleTransitionEnd);
            this._element.removeEventListener("touchstart", this._handleTouchStart);
            this._element.removeEventListener("touchend", this._handleTouchEnd);
        }
    }

    private _handleTouchStart = (event: TouchEvent) => {
        this._touchStartX = event.changedTouches[0].clientX;
    }

    private _handleTouchEnd = (event: TouchEvent) => {

        const dragDistance = Math.abs(event.changedTouches[0].clientX - this._touchStartX);

        if (dragDistance < this._touchDragThreshold) return;

        if (event.changedTouches[0].clientX < this._touchStartX) {
            this.moveNext();
        } else {
            this.movePrevious();
        }
    }

    private _handleTransitionEnd = (event: TransitionEvent) => {

        /** this._element is the swiper element and
         * if any element inside has any kind of transitions on their transform css property...
         * their "transitionend" events will also be catched here
         * So we check the event target because
         * we want to run this handler only for events that have this._element as target */
        if (event.propertyName === "transform" && event.target === this._element) {

            if (this._swipeDirection < 0) {

                this._element.append(this._element.firstElementChild);

                this._element.style.transition = "none";
                this._element.style.transform = "translateX(0)";
            }

            this._setIsNavigationAllowed(true);
        }
    };

    @action.bound
    private _setIsNavigationAllowed(allowed: boolean) {
        this.allowNext = allowed;
        this.allowPrevious = allowed;
    }

    setElement(element: HTMLElement): void {
        if (element) {
            this._element = element;
            this._element.style.overflow = "initial";
            this._subscribe();
        } else {
            this._element = null;
            this._unsubscribe();
        }
    }

    moveNext(options?: ISwipeLogicHandlerMoveNextOptions): void {

        if (!this.allowNext) return;

        options = {
            moveOnVideoFinished: false,
            ...options
        };

        this._reportSwipeToAnalytics(true, options.moveOnVideoFinished);

        this._swipeDirection = -1;
        this._element.style.transition = this._swipeTransitionCss;
        const translateAmount = (this._services.mediaQuery.xlAndAbove ? 33.33 : 100) * (this._services.language.isRTLLanguage ? 1 : -1);
        this._element.style.transform = "translateX(" + translateAmount + "%)";
        this._setIsNavigationAllowed(false);
    }

    movePrevious(): void {

        if (!this.allowPrevious) return;

        this._reportSwipeToAnalytics(false);

        this._swipeDirection = 1;

        this._element.style.transition = "none";
        this._element.prepend(this._element.lastElementChild);
        const translateAmount = (this._services.mediaQuery.xlAndAbove ? 33.33 : 100) * (this._services.language.isRTLLanguage ? 1 : -1);
        this._element.style.transform = "translateX(" + translateAmount + "%)";

        this._setIsNavigationAllowed(false);

        requestAnimationFrame(() => {
            this._element.style.transition = this._swipeTransitionCss;
            this._element.style.transform = "translateX(0)";
        });
    }

    resetScrollPosition(): void {
        this._element.style.transform = "translateX(0)";
    }

    private _currentGameIndex: NullableNumber = null;
    setCurrentGameIndex(gameIndex: number): void {
        this._currentGameIndex = gameIndex;
    }

    private _reportSwipeToAnalytics(swipeToLeft:boolean, isAutoSwipe:boolean=false): void {
        try {

        if(Check.isNullOrUndefined(this._currentGameIndex)) {
            return;
        }

        let nextGame;
        if(swipeToLeft === true){
            nextGame =  (this._currentGameIndex + 1) % this._gamesListViewModel.gameTypes.length;
        }
        else {
           nextGame = (this._currentGameIndex>0) ? (this._currentGameIndex-1) : (this._gamesListViewModel.gameTypes.length-1);
        }

        let swipeFromGameId = this._gamesListViewModel.gameTypes[this._currentGameIndex];

        this._services.analytics.sendElasticInfoEvent({
            eventType:"CGP_GENERIC_EVENT",
            category: this._gamesListViewModel.arenaName,
            label: 'SWIPE',
            action: "LIVE_CASINO_VIDEO",
            customData: {
                swipeFromGameId:swipeFromGameId,
                swipeFromGameIndex:this._currentGameIndex,
                swipeToGameId:this._gamesListViewModel.gameTypes[nextGame],
                isAutoSwipe:isAutoSwipe
            }
        }, true);
        }
        catch (err){
            console.log("SWIPE analytics report failed : " + err);
        }
    }

}
